import { SITE_URL, SITE_NAME } from '../config/site';

const LOGO_URL = `${SITE_URL}/logo.png`;

export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: LOGO_URL,
  description: 'CareerBuddyPro connects world-class African professionals with global companies.',
  sameAs: [
    'https://twitter.com/careerbuddypro',
    'https://www.linkedin.com/company/careerbuddypro',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: `${SITE_URL}/contact`,
  },
});

export const getWebSiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/search/jobs?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

export const getJobPostingSchema = (job) => {
  if (!job) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description,
    datePosted: job.created_at || job.createdAt || new Date().toISOString(),
    hiringOrganization: {
      '@type': 'Organization',
      name: job.company_name || job.companyName || SITE_NAME,
      sameAs: SITE_URL,
    },
    jobLocationType: 'TELECOMMUTE',
  };

  if (job.budget_min || job.budget_max || job.budget) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: 'USD',
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.budget_min || job.budget,
        maxValue: job.budget_max || job.budget,
        unitText: job.budget_type === 'hourly' ? 'HOUR' : 'YEAR',
      },
    };
  }

  if (job.skills_required?.length) {
    schema.skills = job.skills_required.map((s) =>
      typeof s === 'object' ? s.skill || s.name : s
    ).join(', ');
  }

  return schema;
};

export const getFreelancerProfileSchema = (freelancer) => {
  if (!freelancer) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: freelancer.name || freelancer.full_name || `${freelancer.first_name || ''} ${freelancer.last_name || ''}`.trim(),
    jobTitle: freelancer.title || freelancer.professional_title || 'Freelancer',
    description: freelancer.bio || freelancer.overview || '',
    url: `${SITE_URL}/freelancers/${freelancer.id || freelancer._id}`,
    worksFor: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    knowsAbout: (freelancer.skills || []).map((s) =>
      typeof s === 'object' ? s.skill || s.name : s
    ),
  };
};

export const getServiceSchema = (service) => {
  if (!service) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
};

export const getBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url ? `${SITE_URL}${item.url}` : undefined,
  })),
});

export const getFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});
